import type { Job, User } from '@core/generated/gql-types';

export const EMPTY_CELL_LABEL = '—';
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
});

export const formatCents = (cents?: number | null): string =>
  typeof cents === 'number'
    ? currencyFormatter.format(cents / 100)
    : EMPTY_CELL_LABEL;

/** `READY_FOR_PAY` -> `Ready For Pay` */
export const formatEnumLabel = (value?: string | null): string => {
  if (!value) {
    return EMPTY_CELL_LABEL;
  }
  return value
    .split('_')
    .map((chunk) => chunk.charAt(0) + chunk.slice(1).toLowerCase())
    .join(' ');
};

const padDatePart = (value: number) => `${value}`.padStart(2, '0');

/** Local `YYYY-MM-DD`, the value format of `<input type="date">`. */
export const toDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate(),
  )}`;

/** Parses a `YYYY-MM-DD` input value as local midnight. */
export const parseDateInputValue = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return Number.isNaN(date.valueOf()) ? null : date;
};

const simpleTimePattern = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s?(am|pm))?$/i;

/** Parses `HH:mm[:ss][ am|pm]`. */
export const parseSimpleTime = (
  value: string,
): { hours: number; minutes: number; seconds: number } | null => {
  const match = simpleTimePattern.exec(value.trim());
  if (!match) {
    return null;
  }
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] ? Number(match[3]) : 0;
  const meridiem = match[4]?.toLowerCase();
  if (minutes >= 60 || seconds >= 60) {
    return null;
  }
  if (meridiem === 'pm' && hours < 12) {
    hours += 12;
  } else if (meridiem === 'am' && hours === 12) {
    hours = 0;
  }
  if (!meridiem && hours >= 24) {
    return null;
  }
  return { hours, minutes, seconds };
};

/**
 * Normalises the mixed timestamp shapes the API returns (epoch ms, epoch
 * seconds, numeric strings, ISO strings, `HH:mm` times) to epoch ms.
 * A bare time resolves to ms since midnight so it still sorts correctly.
 */
export const parseTimestamp = (
  value?: string | number | null,
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return value > 1e9 && value < 1e12 ? value * 1000 : value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const numeric = Number(trimmed);
  if (Number.isFinite(numeric)) {
    return parseTimestamp(numeric);
  }
  const simpleTime = parseSimpleTime(trimmed);
  if (simpleTime) {
    return (
      (simpleTime.hours * 3600 + simpleTime.minutes * 60 + simpleTime.seconds) *
      1000
    );
  }
  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

export const formatDateLabel = (value?: string | number | null): string => {
  const timestamp = parseTimestamp(value);
  return timestamp === null
    ? EMPTY_CELL_LABEL
    : new Date(timestamp).toLocaleDateString('en-AU');
};

export const formatDateTimeLabel = (value?: string | number | null): string => {
  const timestamp = parseTimestamp(value);
  return timestamp === null
    ? EMPTY_CELL_LABEL
    : new Date(timestamp).toLocaleString('en-AU');
};

export const formatDateRangeLabel = (
  from?: string | number | null,
  to?: string | number | null,
): string => {
  const fromLabel = formatDateLabel(from);
  const toLabel = formatDateLabel(to);
  if (fromLabel !== EMPTY_CELL_LABEL && toLabel !== EMPTY_CELL_LABEL) {
    return `${fromLabel} → ${toLabel}`;
  }
  return fromLabel !== EMPTY_CELL_LABEL ? fromLabel : toLabel;
};

export const formatTimeLabel = (value?: string | number | null): string => {
  if (!value) {
    return EMPTY_CELL_LABEL;
  }
  if (typeof value === 'string') {
    const simple = parseSimpleTime(value);
    if (simple) {
      return `${padDatePart(simple.hours)}:${padDatePart(simple.minutes)}`;
    }
  }
  const timestamp = parseTimestamp(value);
  if (!timestamp) {
    return EMPTY_CELL_LABEL;
  }
  return new Date(timestamp).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const formatPersonName = (
  person?: Pick<User, 'id' | 'firstName' | 'lastName' | 'email'> | null,
  fallback = 'Unknown worker',
): string => {
  if (!person) {
    return fallback;
  }
  const fullName = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
  return fullName || person.email || person.id || fallback;
};

/**
 * Worker pay or client charge rate for a job: the final rate when set,
 * otherwise the best available potential rate.
 */
export const resolveJobRateCents = (
  job: Job | undefined,
  type: 'worker' | 'client',
): number | null => {
  if (!job) {
    return null;
  }
  const direct =
    type === 'worker'
      ? job.finalWorkerPayRateCents
      : job.finalClientPayRateCents;
  if (typeof direct === 'number' && direct > 0) {
    return direct;
  }
  const potential = job.potentialPayRates;
  if (!potential) {
    return null;
  }
  const candidates =
    type === 'worker'
      ? [
          potential.potentialWorkerPayRateForUserIdInCents,
          potential.potentialWorkerPayRateMedianInCents,
          potential.potentialWorkerPayRateAverageInCents,
          potential.potentialWorkerPayRateCeilingInCents,
          potential.potentialWorkerPayRateFloorInCents,
        ]
      : [
          potential.potentialClientPayRateMedianInCents,
          potential.potentialClientPayRateAverageInCents,
          potential.potentialClientPayRateCeilingInCents,
          potential.potentialClientPayRateFloorInCents,
        ];
  return (
    candidates.find(
      (entry): entry is number => typeof entry === 'number' && entry > 0,
    ) ?? null
  );
};

export const isHttpUrl = (value?: string | null): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value);
